import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { money } from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/PrefsContext";
import {
  PageHeader, PrimaryButton, Modal, Field, Input, Textarea, SelectBox, Button, StatusBadge,
} from "@/components/ui-kit";
import { IconBtn } from "@/pages/Clients";
import {
  Plus, ChevronLeft, ChevronRight, Pencil, Trash2, CheckCircle2, Clock, MapPin, User,
} from "lucide-react";

const EMPTY = {
  client_id: "", customer_name: "", service: "", employee_id: "",
  date: new Date().toISOString().slice(0, 10), start_time: "09:00", end_time: "11:00",
  status: "Scheduled", amount: 0, address: "", notes: "",
};

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function Schedule() {
  const [jobs, setJobs] = useState([]);
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [cursor, setCursor] = useState(new Date());
  const navigate = useNavigate();
  const { t } = useI18n();

  const load = () => api.get("/jobs").then(({ data }) => setJobs(data));
  useEffect(() => {
    load();
    api.get("/clients").then(({ data }) => setClients(data));
    api.get("/services").then(({ data }) => setServices(data));
    api.get("/employees").then(({ data }) => setEmployees(data));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = (date) => { setForm({ ...EMPTY, date: date || EMPTY.date }); setEditing(null); setOpen(true); };
  const openEdit = (j) => { setForm({ ...EMPTY, ...j }); setEditing(j.id); setOpen(true); };

  const pickClient = (cid) => {
    const c = clients.find((x) => x.id === cid);
    if (c) setForm((f) => ({ ...f, client_id: cid, customer_name: c.name, address: c.address }));
    else set("client_id", "");
  };
  const pickService = (name) => {
    const s = services.find((x) => x.name === name);
    setForm((f) => ({ ...f, service: name, amount: f.amount || (s ? s.base_price : 0) }));
  };

  const save = async () => {
    if (!form.customer_name && !form.client_id) return toast.error("Select a client or enter a customer name");
    if (!form.service) return toast.error("Select a service");
    const payload = { ...form, amount: Number(form.amount) };
    try {
      if (editing) await api.put(`/jobs/${editing}`, payload);
      else await api.post("/jobs", payload);
      toast.success(editing ? t("Job updated") : t("Job scheduled"));
      setOpen(false); load();
    } catch { toast.error("Could not save job"); }
  };

  const complete = async (j) => {
    if (!window.confirm(`Mark ${j.job_number} complete? This generates an invoice automatically.`)) return;
    try {
      const { data } = await api.post(`/jobs/${j.id}/complete`);
      toast.success(t("Job completed — invoice generated"));
      load();
      if (data.invoice_id) setTimeout(() => navigate("/invoices"), 600);
    } catch { toast.error("Could not complete job"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this job?")) return;
    await api.delete(`/jobs/${id}`); toast.success(t("Job deleted")); load();
  };

  // Calendar grid
  const y = cursor.getFullYear(), m = cursor.getMonth();
  const first = new Date(y, m, 1);
  const startDay = first.getDay();
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const jobsByDate = {};
  jobs.forEach((j) => { (jobsByDate[j.date] = jobsByDate[j.date] || []).push(j); });
  const fmt = (d) => `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div data-testid="schedule-page">
      <PageHeader title="Schedule" subtitle="Plan cleanings, assign crew, and complete jobs to auto-generate invoices."
        action={<PrimaryButton onClick={() => openNew()} data-testid="add-job-button"><Plus size={16} /> {t("Schedule Job")}</PrimaryButton>} />

      <div className="mt-card p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display text-xl font-semibold text-[#121212]">
            {cursor.toLocaleString("default", { month: "long" })} {y}
          </h3>
          <div className="flex gap-1">
            <IconBtn onClick={() => setCursor(new Date(y, m - 1, 1))} title="Previous"><ChevronLeft size={18} /></IconBtn>
            <button onClick={() => setCursor(new Date())} className="px-3 text-sm font-medium text-[#5A189A] hover:text-[#D4AF37]">{t("Today")}</button>
            <IconBtn onClick={() => setCursor(new Date(y, m + 1, 1))} title="Next"><ChevronRight size={18} /></IconBtn>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-1 mb-1">
          {DOW.map((d) => <div key={d} className="text-center text-xs font-semibold text-[#9b8bb5] py-2">{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((d, i) => {
            if (!d) return <div key={i} className="min-h-[92px]" />;
            const date = fmt(d);
            const dayJobs = jobsByDate[date] || [];
            const isToday = date === today;
            return (
              <div key={i} onClick={() => openNew(date)}
                className={`min-h-[92px] rounded-xl p-1.5 border cursor-pointer transition hover:border-[#5A189A]/40 ${isToday ? "border-[#D4AF37] bg-[#fdfaf0]" : "border-[#f0ebf7] bg-white"}`}>
                <div className={`text-xs font-semibold mb-1 ${isToday ? "text-[#D4AF37]" : "text-[#52525B]"}`}>{d}</div>
                <div className="space-y-1">
                  {dayJobs.slice(0, 3).map((j) => (
                    <div key={j.id} onClick={(e) => { e.stopPropagation(); openEdit(j); }}
                      className="text-[10px] leading-tight px-1.5 py-1 rounded-md truncate text-white"
                      style={{ background: j.status === "Completed" ? "#16a34a" : j.status === "In Progress" ? "#D4AF37" : "#7B2CBF" }}
                      title={`${j.customer_name} · ${j.service}`}>
                      {j.start_time} {j.customer_name}
                    </div>
                  ))}
                  {dayJobs.length > 3 && <div className="text-[10px] text-[#9b8bb5] px-1">+{dayJobs.length - 3} more</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Upcoming list */}
      <div className="mt-card p-6 mt-6">
        <h3 className="font-display text-lg font-semibold text-[#121212] mb-3">{t("All Jobs")}</h3>
        {jobs.length === 0 ? (
          <div className="text-sm text-[#9b8bb5] py-4">{t("No jobs scheduled yet.")}</div>
        ) : (
          <div className="divide-y divide-[#f4f0fa]">
            {jobs.map((j) => (
              <div key={j.id} className="flex flex-wrap items-center gap-3 py-3">
                <div className="w-10 h-10 rounded-xl bg-[#5A189A]/10 text-[#5A189A] flex items-center justify-center text-xs font-semibold">{j.job_number?.replace("JOB-", "#")}</div>
                <div className="flex-1 min-w-[140px]">
                  <div className="font-medium text-sm text-[#121212] flex items-center gap-2"><User size={13} className="text-[#9b8bb5]" /> {j.customer_name}</div>
                  <div className="text-xs text-[#52525B] flex items-center gap-3 mt-0.5">
                    <span>{j.service}</span>
                    <span className="flex items-center gap-1"><Clock size={11} /> {j.date} {j.start_time}</span>
                    {j.employee_name && <span className="flex items-center gap-1"><User size={11} /> {j.employee_name}</span>}
                  </div>
                </div>
                <span className="font-medium text-sm">{money(j.amount)}</span>
                <StatusBadge status={j.status} />
                <div className="flex items-center gap-1">
                  {j.status !== "Completed" && <IconBtn onClick={() => complete(j)} title="Complete & Invoice" testid={`complete-job-${j.id}`}><CheckCircle2 size={16} /></IconBtn>}
                  <IconBtn onClick={() => openEdit(j)} title="Edit"><Pencil size={16} /></IconBtn>
                  <IconBtn onClick={() => remove(j.id)} title="Delete" danger><Trash2 size={16} /></IconBtn>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit Job" : "Schedule Job"} size="lg"
        footer={<><Button variant="outline" onClick={() => setOpen(false)}>{t("Cancel")}</Button><PrimaryButton onClick={save} data-testid="save-job-button">{t("Save Job")}</PrimaryButton></>}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Client"><SelectBox value={form.client_id} onChange={(e) => pickClient(e.target.value)} options={clients.map((c) => ({ value: c.id, label: c.name }))} placeholder="Select client" /></Field>
          <Field label="Customer Name"><Input value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} data-testid="job-customer-input" /></Field>
          <Field label="Service"><SelectBox value={form.service} onChange={(e) => pickService(e.target.value)} options={services.map((s) => s.name)} placeholder="Select service" /></Field>
          <Field label="Assigned Employee"><SelectBox value={form.employee_id} onChange={(e) => set("employee_id", e.target.value)} options={employees.map((emp) => ({ value: emp.id, label: emp.name }))} placeholder="Unassigned" /></Field>
          <Field label="Date"><Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Start"><Input type="time" value={form.start_time} onChange={(e) => set("start_time", e.target.value)} /></Field>
            <Field label="End"><Input type="time" value={form.end_time} onChange={(e) => set("end_time", e.target.value)} /></Field>
          </div>
          <Field label="Amount ($)"><Input type="number" value={form.amount} onChange={(e) => set("amount", e.target.value)} /></Field>
          <Field label="Status"><SelectBox value={form.status} onChange={(e) => set("status", e.target.value)} options={["Scheduled", "In Progress", "Completed", "Cancelled"]} /></Field>
          <Field label="Address" className="sm:col-span-2"><Input value={form.address} onChange={(e) => set("address", e.target.value)} /></Field>
          <Field label="Notes" className="sm:col-span-2"><Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></Field>
        </div>
      </Modal>
    </div>
  );
}
